import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { IField } from '../../../data-models/field2.data-model';
import {
  IKPITemplate,
  IKPITemplateSectionDataModel,
  ITextualKpiTemplateSectionMeta,
} from '../../../data-models/kpi-template.data-model';
import { isTextualKpiField, textualKpiConfigAtom } from '../../../services/state/KPIConfigState';
import { IKPIDataValue } from '../../../data-models/kpi-requests.data-model';

export function useTextualKpiFields(existingSections: IKPITemplateSectionDataModel[]) {
  const allTextualKpis = useAtomValue(textualKpiConfigAtom);
  const availableTextualKpis = useMemo(() => {
    const selectedTextual = existingSections?.reduce((acc, s) => {
      const kpiConfig = (s?.meta as ITextualKpiTemplateSectionMeta)?.kpiConfig;
      if (kpiConfig && isTextualKpiField(kpiConfig) && kpiConfig.id != null) {
        acc.add(kpiConfig.id);
      }
      return acc;
    }, new Set<number>());

    return allTextualKpis.filter((kpi) => !selectedTextual.has(kpi.id));
  }, [allTextualKpis, existingSections]);
  const disableTextualKpis = useMemo(() => availableTextualKpis.length === 0, [availableTextualKpis]);

  return { availableTextualKpis, disableTextualKpis };
}

export function getTextualKpiOptions(
  section: IKPITemplateSectionDataModel | IField<unknown>,
  availableTextualKpis: IField<unknown>[]
) {
  const selectedTextualKpi = (
    (section as IKPITemplateSectionDataModel)?.meta as ITextualKpiTemplateSectionMeta
  )?.kpiConfig;
  return selectedTextualKpi ? [selectedTextualKpi, ...availableTextualKpis] : availableTextualKpis;
}

export function getTemplateTextualKpisMap(template: IKPITemplate) {
  return (template.sections ?? []).reduce((map, section) => {
    const kpiConfig = (section.meta as ITextualKpiTemplateSectionMeta)?.kpiConfig;
    if (kpiConfig) {
      map.set(kpiConfig.id, kpiConfig);
    }
    return map;
  }, new Map<number, IField<unknown>>());
}

export function mergeUpdatedTextualKpiData(updatedKpi: IKPIDataValue, currentData: IKPIDataValue[]) {
  let found = false;
  const newData = currentData.map((data) => {
    if (data.kpiId === updatedKpi.kpiId && data.section === updatedKpi.section) {
      found = true;
      return updatedKpi;
    }
    return data;
  });
  if (!found) {
    newData.push(updatedKpi);
  }
  return newData;
}
